import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { AnalyticsDestination } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useDeleteAnalyticsDestination } from '../hooks/use-analytics-destination-mutations';

export interface DeleteDestinationDialogProps {
  storeId: string;
  destination: AnalyticsDestination | null;
  onOpenChange: (open: boolean) => void;
}

export function DeleteDestinationDialog({
  storeId,
  destination,
  onOpenChange,
}: DeleteDestinationDialogProps) {
  const { t } = useTranslation(['analytics', 'common']);
  const remove = useDeleteAnalyticsDestination(storeId);
  const message = useErrorMessage();
  const provider = destination ? t(`analytics:providers.${destination.provider}`) : '';

  return (
    <AlertDialog
      open={destination !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('analytics:delete.title', { provider })}</AlertDialogTitle>
          <AlertDialogDescription>{t('analytics:delete.description')}</AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{message(remove.error)}</AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (destination) {
                remove.mutate(destination.id, { onSuccess: () => onOpenChange(false) });
              }
            }}
          >
            {remove.isPending ? t('analytics:delete.submitting') : t('analytics:delete.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
